import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, mergeMap, withLatestFrom, tap } from 'rxjs/operators';
import { ObservableInput } from 'observable-input';
import { ZefError, zefRemoveError } from '@zerops/zef/errors';
import { authActions, SUDO_ERROR_CODE } from '@zerops/zef/auth';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { loadUser } from '@zerops/zerops/core/user-base';

@Component({
  selector: 'z-dialog-error',
  templateUrl: './dialog-error.container.html',
  styleUrls: [ './dialog-error.container.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DialogErrorContainer extends ZefReactiveComponent {

  /**
   * Sync Data
   */
  showButton = false;
  userLoadKey = loadUser.type;
  SUDO_ERROR_CODE = SUDO_ERROR_CODE;

  /**
   * Event Streams
   */
  onLogout$ = new Subject<void>();
  onClose$ = new Subject<void>();

  @ObservableInput()
  /* eslint-disable-next-line @angular-eslint/no-input-rename */
  @Input('data')
  data$!: Observable<ZefError[]>;

  errors$ = this.data$.pipe(
    map((errors) => {
      // Handle null/undefined errors array from delayed stream
      if (!errors || !Array.isArray(errors)) {
        return [];
      }
      // Deduplication: Show ONE sudo UI even if multiple actions failed with sudo errors
      // User's authentication will retry only the FIRST failing action (via originalAction)
      // Other failed actions remain in error state and require separate user interaction
      const sudoError = errors.find((e) => e.code === SUDO_ERROR_CODE);
      const otherErrors = errors.filter((e) => e.code !== SUDO_ERROR_CODE);
      return sudoError ? [ sudoError, ...otherErrors ] : otherErrors;
    }),
    tap((errors) => {
      this.showButton = errors.some((error) =>
        error.originalError?.status !== 403 &&
        error.key !== this.userLoadKey &&
        error.code !== SUDO_ERROR_CODE
      );
    })
  );

  /**
   * Action Streams
   */

  private _logoutAction$ = this.onLogout$.pipe(
    map(() => authActions.logout())
  );

  private _closeAction$ = this.onClose$.pipe(
    withLatestFrom(this.errors$),
    mergeMap(([ _, errors ]) => errors.map((error) => zefRemoveError(error.key)))
  );

  constructor() {
    super();

    this.$dispatchActions([
      this._logoutAction$,
      this._closeAction$
    ]);
  }

}
